import React, { useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import Table from "../../../../core/common/dataTable";
import { useUsers } from "./hooks/useUsers";
import { UserDetails } from "./types/userResponse";
import TooltipOption from "../../../../core/common/tooltipOption";
import CommonSelect from "../../../../core/common/commonSelect";
// import PredefinedDateRanges from "../../../../core/common/datePicker";
import { status } from "../../../../core/common/selectoption/selectoption";
import UserModals from "./userModals";
import { useCenter } from "../hooks/useCenter";

const CenterUsers = () => {
  const navigate = useNavigate();
  const { id: centerId } = useParams<{ id: string }>();
  const { useGetCenterById } = useCenter({ skip: true });
  const { data: centerDetails, isLoading: isCenterLoading } = useGetCenterById(
    centerId || "0"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const routes = all_routes;
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedUsername, setSelectedUsername] = useState<string>("");
  const [showModals, setShowModals] = useState({
    add: false,
    edit: false,
    delete: false,
    changePassword: false,
    deactivate: false,
    activate: false,
  });

  const { getUsersState, paginationData } = useUsers({
    centerId: parseInt(centerId || "0"),
    page: currentPage,
    size: recordsPerPage,
    search: search,
  });

  const { users, isLoading, error } = getUsersState();

  const handlePageChange = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    if (pageSize) {
      setRecordsPerPage(pageSize);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
  };

  const handleModalClose = () => {
    const modals = document.querySelectorAll(".modal");
    modals.forEach((modal) => {
      modal.classList.remove("show");
      if (modal instanceof HTMLElement) {
        modal.style.display = "none";
      }
    });
    const backdrop = document.querySelector(".modal-backdrop");
    if (backdrop) {
      backdrop.remove();
    }
    document.body.classList.remove("modal-open");
    setShowModals({
      add: false,
      edit: false,
      delete: false,
      changePassword: false,
      deactivate: false,
      activate: false,
    });
    setSelectedUserId("");
  };

  const handleEditClick = (userId: string) => {
    const editUrl = all_routes.editUser
      .replace(":id", centerId || "")
      .replace(":userId", userId);
    navigate(editUrl);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedUserId(id);
    const modal = document.getElementById("delete-modal");
    if (modal instanceof HTMLElement) {
      modal.classList.add("show");
      modal.style.display = "block";
      document.body.classList.add("modal-open");
      const backdrop = document.createElement("div");
      backdrop.className = "modal-backdrop fade show";
      document.body.appendChild(backdrop);
    }
    setShowModals((prev) => ({
      ...prev,
      delete: true,
    }));
  };

  const handleDeactivateClick = (id: string) => {
    setSelectedUserId(id);
    const modal = document.getElementById("deactivate-modal");
    if (modal instanceof HTMLElement) {
      modal.classList.add("show");
      modal.style.display = "block";
      document.body.classList.add("modal-open");
      const backdrop = document.createElement("div");
      backdrop.className = "modal-backdrop fade show";
      document.body.appendChild(backdrop);
    }
    setShowModals((prev) => ({
      ...prev,
      deactivate: true,
    }));
  };

  const handleActivateClick = (id: string) => {
    setSelectedUserId(id);
    const modal = document.getElementById("activate-modal");
    if (modal instanceof HTMLElement) {
      modal.classList.add("show");
      modal.style.display = "block";
      document.body.classList.add("modal-open");
      const backdrop = document.createElement("div");
      backdrop.className = "modal-backdrop fade show";
      document.body.appendChild(backdrop);
    }
    setShowModals((prev) => ({
      ...prev,
      activate: true,
    }));
  };

  const handleChangePasswordClick = (userId: string, username: string) => {
    setSelectedUserId(userId);
    setSelectedUsername(username);
    setShowModals((prev) => ({
      ...prev,
      changePassword: true,
    }));
  };

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const handleSuccess = () => {
    handleModalClose();

    setCurrentPage(1);
  };

  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1,
      sorter: (a: UserDetails, b: UserDetails) => a.id - b.id,
    },
    {
      title: "Username",
      dataIndex: "username",
      render: (text: string, record: UserDetails) => (
        <div className="d-flex align-items-center">
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link to="#">{record.username}</Link>
            </p>
          </div>
        </div>
      ),
      sorter: (a: UserDetails, b: UserDetails) =>
        a.username.localeCompare(b.username),
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (role: string) => (
        <span
          className={`badge badge-soft-${
            role === "centeradmin" ? "primary" : "info"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {role}
        </span>
      ),
      sorter: (a: UserDetails, b: UserDetails) => a.role.localeCompare(b.role),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge badge-soft-${
            text === "active" ? "success" : "danger"
          } d-inline-flex align-items-center`}
        >
          <i className="ti ti-circle-filled fs-5 me-1"></i>
          {text === "inactive" ? "Deactivated" : text}
        </span>
      ),
      sorter: (a: UserDetails, b: UserDetails) =>
        a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      render: (_: string, record: UserDetails) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            {record.status === "active" ? (
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    to={all_routes.editUser
                      .replace(":id", centerId || "")
                      .replace(":userId", record.id.toString())}
                    className="dropdown-item rounded-1"
                  >
                    <i className="ti ti-edit me-2" />
                    Edit
                  </Link>
                </li>
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    data-bs-toggle="modal"
                    data-bs-target="#change-password-modal"
                    onClick={() =>
                      handleChangePasswordClick(
                        record.id.toString(),
                        record.username
                      )
                    }
                  >
                    <i className="ti ti-key me-2" />
                    Change Password
                  </Link>
                </li>
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    onClick={() => handleDeactivateClick(record.id.toString())}
                  >
                    <i className="ti ti-user-off me-2" />
                    Deactivate
                  </Link>
                </li>
                {record.role !== "student" && (
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-1"
                      onClick={() => handleDeleteClick(record.id.toString())}
                    >
                      <i className="ti ti-trash me-2" />
                      Delete
                    </Link>
                  </li>
                )}
              </ul>
            ) : (
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    to="#"
                    className="dropdown-item rounded-1"
                    onClick={() => handleActivateClick(record.id.toString())}
                  >
                    <i className="ti ti-refresh me-2" />
                    Activate
                  </Link>
                </li>
              </ul>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {centerDetails?.data?.center?.name
                ? `${centerDetails.data.center.name} - Users`
                : "Center Users"}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.centerList}>Centers</Link>
                </li>
                <li className="breadcrumb-item active">Users</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            {/* <TooltipOption /> */}
            <div className="mb-2 d-flex gap-2">
              <Link
                to={all_routes.centerDetailsId.replace(":id", centerId || "")}
                className="btn btn-primary d-flex align-items-center"
              >
                <i className="ti ti-arrow-left me-2" />
                Back to Details
              </Link>
              <Link
                to={all_routes.addUser.replace(":id", centerId || "")}
                className="btn btn-primary d-flex align-items-center"
              >
                <i className="ti ti-square-rounded-plus me-2" />
                Add User
              </Link>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Users List</h4>
            <div className="d-flex align-items-center flex-wrap">
              {/* <div className="input-icon-start mb-3 me-2 position-relative">
                <PredefinedDateRanges />
              </div> */}
              <div className="dropdown mb-3 me-2">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                >
                  <i className="ti ti-filter me-2" />
                  Filter
                </Link>
                <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                  <form>
                    <div className="d-flex align-items-center border-bottom p-3">
                      <h4>Filter</h4>
                    </div>
                    <div className="p-3 pb-0 border-bottom">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Role</label>
                            <CommonSelect
                              className="select"
                              options={[
                                { value: "all", label: "All" },
                                { value: "centeradmin", label: "Center Admin" },
                                { value: "user", label: "User" },
                              ]}
                              defaultValue={{ value: "all", label: "All" }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Status</label>
                            <CommonSelect
                              className="select"
                              options={status}
                              defaultValue={status[0]}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 d-flex align-items-center justify-content-end">
                      <Link to="#" className="btn btn-light me-3">
                        Reset
                      </Link>
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={handleApplyClick}
                      >
                        Apply
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
              <div className="dropdown mb-3">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-sort-ascending-2 me-2" />
                  Sort by A-Z
                </Link>
                <ul className="dropdown-menu p-3">
                  <li>
                    <Link to="#" className="dropdown-item rounded-1 active">
                      Ascending
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="dropdown-item rounded-1">
                      Descending
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="card-body p-0 py-3">
            {isLoading ? (
              <div className="text-center">Loading users...</div>
            ) : error ? (
              <div className="alert alert-danger m-3">
                Error loading users: {error.toString()}
              </div>
            ) : (
              <Table
                dataSource={users}
                columns={columns}
                Selection={false}
                onSearchChange={handleSearchChange}
                pagination={{
                  current: currentPage,
                  pageSize: recordsPerPage,
                  total: paginationData?.totalItems || 0,
                  onChange: handlePageChange,
                  pageSizeOptions: ["All", 10, 20, 30],
                }}
              />
            )}
          </div>
        </div>
      </div>

      <UserModals
        userId={selectedUserId}
        username={selectedUsername}
        centerId={parseInt(centerId || "0")}
        show={showModals}
        onHide={handleModalClose}
        onSuccess={handleSuccess}
      />
    </div>
  );
};

export default CenterUsers;
